import { BadgeCheck, Download, RefreshCw } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { formatMinutes, todayKey } from "../../shared/calculations";
import { selectableActivitiesForOrder } from "../../shared/jobActivities";
import type { ActivityType, BootstrapData, JobTimeEntry, JobTimeSyncRun, Order } from "../../shared/types";

type DifferenceRow = {
  employee_id: string;
  employee_name: string;
  date: string;
  net_work_minutes: number;
  job_minutes: number;
  difference_minutes: number;
  warning: "overbooked" | "unassigned" | "ok";
};

type Payload = {
  orders: Order[];
  activity_types: ActivityType[];
  entries: JobTimeEntry[];
  sync_runs: JobTimeSyncRun[];
  difference_report: DifferenceRow[];
};

async function api<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: "Anfrage fehlgeschlagen" }));
    throw new Error(payload.error ?? "Anfrage fehlgeschlagen");
  }
  return response.json() as Promise<T>;
}

export function JobTimesBackoffice({ data, refresh }: { data: BootstrapData; refresh: () => Promise<void> }) {
  const [section, setSection] = useState<"orders" | "today" | "detail" | "employees" | "manual" | "sync" | "diff" | "export">("orders");
  const [payload, setPayload] = useState<Payload | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [manual, setManual] = useState({
    employeeId: data.employees[0]?.id ?? "",
    orderId: "",
    activityTypeId: "",
    date: data.today,
    minutes: 0,
    comment: ""
  });

  async function load() {
    const result = await api<Payload>("/api/job-times/backoffice");
    setPayload(result);
    setSelectedOrderId((current) => current || result.orders[0]?.id || "");
    setSelectedEmployeeId((current) => current || data.employees[0]?.id || "");
    setManual((current) => ({
      ...current,
      orderId: current.orderId || result.orders[0]?.id || "",
      activityTypeId: current.activityTypeId || result.activity_types[0]?.id || ""
    }));
  }

  useEffect(() => {
    void load().catch((error) => setMessage(error instanceof Error ? error.message : "Auftragszeiten konnten nicht geladen werden."));
  }, []);

  const entries = payload?.entries ?? data.job_time_entries ?? [];
  const orders = payload?.orders ?? data.orders ?? [];
  const activities = payload?.activity_types ?? data.activity_types ?? [];
  const selectedOrder = orders.find((order) => order.id === selectedOrderId) ?? orders[0];
  const selectedEmployee = data.employees.find((employee) => employee.id === selectedEmployeeId) ?? data.employees[0];
  const todayEntries = entries.filter((entry) => entry.date === data.today && entry.status !== "cancelled");
  const selectedOrderEntries = selectedOrder ? entries.filter((entry) => entry.order_id === selectedOrder.id) : [];
  const selectedEmployeeEntries = selectedEmployee ? entries.filter((entry) => entry.employee_id === selectedEmployee.id) : [];
  const allowedActivities = useMemo(() => activitiesForOrder(selectedOrder, activities), [selectedOrder, activities]);

  useEffect(() => {
    if (!allowedActivities.length) return;
    if (!allowedActivities.some((activity) => activity.id === manual.activityTypeId)) {
      setManual((current) => ({ ...current, activityTypeId: allowedActivities[0].id }));
    }
  }, [manual.orderId, allowedActivities]);

  async function submitManual(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    try {
      await api("/api/job-times/manual", { method: "POST", body: JSON.stringify(manual) });
      setManual((current) => ({ ...current, minutes: 0, comment: "" }));
      await Promise.all([load(), refresh()]);
      setMessage("Manuelle Auftragszeit gespeichert.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Manuelle Auftragszeit konnte nicht gespeichert werden.");
    }
  }

  async function syncOrders() {
    const result = await api<Record<string, unknown>>("/api/orders/sync", { method: "POST", body: "{}" });
    await Promise.all([load(), refresh()]);
    setMessage(`Auftragssync: ${String(result.imported ?? 0)} Aufträge gelesen.`);
  }

  async function syncYesterday() {
    const result = await api<JobTimeSyncRun>("/api/job-times/sync-yesterday", { method: "POST", body: "{}" });
    await Promise.all([load(), refresh()]);
    setMessage(`Zeitsync ${result.status}: ${formatMinutes(result.delta_minutes)} Delta.`);
  }

  if (!payload) return <section className="wide-panel">Auftragszeiten werden geladen...</section>;

  return (
    <section className="wide-panel jobtimes-admin">
      <div className="jobtimes-tabs">
        {[
          ["orders", "Aktive Aufträge"],
          ["today", "Heute"],
          ["detail", "Auftragsdetail"],
          ["employees", "Mitarbeiter"],
          ["manual", "Korrekturen"],
          ["sync", "Sync-Status"],
          ["diff", "Differenzreport"],
          ["export", "Export"]
        ].map(([key, label]) => (
          <button className={section === key ? "active" : ""} key={key} onClick={() => setSection(key as typeof section)}>
            {label}
          </button>
        ))}
      </div>

      {section === "orders" && <OrdersTable orders={orders} />}
      {section === "today" && <EntriesTable activities={activities} employees={data.employees} entries={todayEntries} orders={orders} />}
      {section === "detail" && (
        <>
          <Selector label="Auftrag" value={selectedOrderId} onChange={setSelectedOrderId} options={orders.map((order) => [order.id, `${order.job_number} - ${order.order_title}`])} />
          <EntriesTable activities={activities} employees={data.employees} entries={selectedOrderEntries} orders={orders} />
        </>
      )}
      {section === "employees" && (
        <>
          <Selector label="Mitarbeiter" value={selectedEmployeeId} onChange={setSelectedEmployeeId} options={data.employees.map((employee) => [employee.id, `${employee.short_code} - ${employee.display_name}`])} />
          <EntriesTable activities={activities} employees={data.employees} entries={selectedEmployeeEntries} orders={orders} />
        </>
      )}
      {section === "manual" && (
        <form className="inline-form correction-form" onSubmit={(event) => void submitManual(event)}>
          <select value={manual.employeeId} onChange={(event) => setManual({ ...manual, employeeId: event.target.value })}>
            {data.employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.short_code} - {employee.display_name}
              </option>
            ))}
          </select>
          <select value={manual.orderId} onChange={(event) => setManual({ ...manual, orderId: event.target.value })}>
            {orders.map((order) => (
              <option key={order.id} value={order.id}>
                {order.job_number} - {order.order_title}
              </option>
            ))}
          </select>
          <select value={manual.activityTypeId} onChange={(event) => setManual({ ...manual, activityTypeId: event.target.value })}>
            {allowedActivities.map((activity) => (
              <option key={activity.id} value={activity.id}>
                {activity.name}
              </option>
            ))}
          </select>
          <input type="date" value={manual.date} onChange={(event) => setManual({ ...manual, date: event.target.value })} />
          <input type="number" value={manual.minutes} onChange={(event) => setManual({ ...manual, minutes: Number(event.target.value) })} />
          <input placeholder="Grund" value={manual.comment} onChange={(event) => setManual({ ...manual, comment: event.target.value })} />
          <button className="button primary" type="submit">
            <BadgeCheck />
            <span>Speichern</span>
          </button>
        </form>
      )}
      {section === "sync" && (
        <>
          <div className="terminal-actions compact-actions">
            <button className="button secondary" onClick={() => void syncOrders()}>
              <RefreshCw />
              <span>Aufträge lesen</span>
            </button>
            <button className="button primary" onClick={() => void syncYesterday()}>
              <RefreshCw />
              <span>Vortag synchronisieren</span>
            </button>
          </div>
          <SyncRunsTable runs={payload.sync_runs} />
        </>
      )}
      {section === "diff" && <DifferenceTable rows={payload.difference_report} />}
      {section === "export" && (
        <div className="export-links">
          <a href="/api/exports/job-times.xlsx">
            <Download /> Auftragszeiten Excel
          </a>
        </div>
      )}
      {message && <p className={message.includes("konnte") || message.includes("fehl") ? "form-error" : "form-success"}>{message}</p>}
    </section>
  );
}

function OrdersTable({ orders }: { orders: Order[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Jobnummer</th>
            <th>Kunde</th>
            <th>Auftrag</th>
            <th>PAL</th>
            <th>Status</th>
            <th>Minuten</th>
            <th>Produktion</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id}>
              <td>{order.job_number}</td>
              <td>{order.customer_name}</td>
              <td>{order.order_title}</td>
              <td>{order.due_date}</td>
              <td>{order.status}</td>
              <td>{order.minutes_current}</td>
              <td>{order.production_activities_json.join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EntriesTable({ entries, orders, employees, activities }: { entries: JobTimeEntry[]; orders: Order[]; employees: BootstrapData["employees"]; activities: ActivityType[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Mitarbeiter</th>
            <th>Auftrag</th>
            <th>Tätigkeit</th>
            <th>Start</th>
            <th>Ende</th>
            <th>Minuten</th>
            <th>Sync</th>
            <th>Kommentar</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td>{entry.date}</td>
              <td>{employees.find((employee) => employee.id === entry.employee_id)?.short_code}</td>
              <td>{orders.find((order) => order.id === entry.order_id)?.job_number}</td>
              <td>{activities.find((activity) => activity.id === entry.activity_type_id)?.name}</td>
              <td>{entry.start_time ? new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(entry.start_time)) : "-"}</td>
              <td>{entry.end_time ? new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(entry.end_time)) : "-"}</td>
              <td>{formatMinutes(entry.duration_minutes, true)}</td>
              <td>{entry.sync_status}</td>
              <td>{entry.comment}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SyncRunsTable({ runs }: { runs: JobTimeSyncRun[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Status</th>
            <th>Einträge</th>
            <th>Delta</th>
            <th>Start</th>
            <th>Fehler</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id}>
              <td>{run.sync_date}</td>
              <td>{run.status}</td>
              <td>{run.entries_count}</td>
              <td>{formatMinutes(run.delta_minutes, true)}</td>
              <td>{new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "short" }).format(new Date(run.started_at))}</td>
              <td>{run.error}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DifferenceTable({ rows }: { rows: DifferenceRow[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Mitarbeiter</th>
            <th>Datum</th>
            <th>Netto</th>
            <th>Auftragszeit</th>
            <th>Differenz</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.employee_id}-${row.date}`}>
              <td>{row.employee_name}</td>
              <td>{row.date}</td>
              <td>{formatMinutes(row.net_work_minutes)}</td>
              <td>{formatMinutes(row.job_minutes)}</td>
              <td>{formatMinutes(row.difference_minutes, true)}</td>
              <td>{row.warning === "overbooked" ? "Warnung: über Nettozeit" : row.warning === "unassigned" ? "nicht zugeordnet" : "ok"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Selector({ label, value, options, onChange }: { label: string; value: string; options: string[][]; onChange: (value: string) => void }) {
  return (
    <label className="inline-selector">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}

function activitiesForOrder(order: Order | undefined, activities: ActivityType[]): ActivityType[] {
  return selectableActivitiesForOrder(order, activities);
}
